/**
 * Personal Map PM-3 — Owner editable Journey／Day selector sheet。
 * 不建立 activity；僅導向既有 Planning／Create Journey。
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { journeyApi } from '../../api/journeyApi';
import { journeyPlanningApi } from '../../api/journeyPlanningApi';
import { Button, Alert } from '../../components/ui';
import { normalizeMyJourneySummary } from '../my-journeys/myJourneysModel';
import { formatPlanningDayHeading } from '../journey-planning/planningPresentation';
import { normalizePlanningSchedule } from '../journey-planning/planningModel';
import {
  createPersonalMapJourneyHandoff,
  filterOwnerEditableJourneys,
  persistHandoffSession,
  wrapHandoffInLocationState,
} from './personalMapJourneyHandoff';

function dayLabel(day, index, t) {
  try {
    const heading = formatPlanningDayHeading(day);
    if (heading) return heading;
  } catch {
    // fall through
  }
  const n = (day?.displayOrder != null ? Number(day.displayOrder) : index + 1) || (index + 1);
  return t('travel.personalMap.addToJourney.dayFallback').replace('{n}', String(n));
}

export default function PersonalMapAddToJourneySheet({
  open,
  seed,
  onClose,
  onFocusRestore,
}) {
  const { t } = useTravelTranslation();
  const navigate = useNavigate();
  const titleId = useId();
  const listId = useId();
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const generation = useRef(0);
  const dayGeneration = useRef(0);
  const mountedRef = useRef(true);

  const [step, setStep] = useState('journey'); // journey | day
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [journeys, setJourneys] = useState([]);
  const [selectedJourneyId, setSelectedJourneyId] = useState('');
  const [daysPhase, setDaysPhase] = useState('idle');
  const [daysError, setDaysError] = useState('');
  const [days, setDays] = useState([]);
  const [selectedDayId, setSelectedDayId] = useState('');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      generation.current += 1;
      dayGeneration.current += 1;
    };
  }, []);

  const loadJourneys = useCallback(async () => {
    const token = ++generation.current;
    setPhase('loading');
    setError('');
    setSelectedJourneyId('');
    setStep('journey');
    setDays([]);
    setSelectedDayId('');
    setDaysPhase('idle');
    setDaysError('');
    try {
      const raw = await journeyApi.listMine();
      if (token !== generation.current || !mountedRef.current) return;
      const list = Array.isArray(raw) ? raw.map(normalizeMyJourneySummary) : [];
      setJourneys(filterOwnerEditableJourneys(list));
      setPhase('ready');
    } catch {
      if (token !== generation.current || !mountedRef.current) return;
      setError(t('travel.personalMap.addToJourney.loadFailed'));
      setPhase('error');
    }
  }, [t]);

  useEffect(() => {
    if (!open) return undefined;
    loadJourneys();
    return () => {
      generation.current += 1;
      dayGeneration.current += 1;
    };
  }, [open, loadJourneys]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => closeRef.current?.focus?.(), 0);

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKeyDown, true);
      window.clearTimeout(timer);
    };
    // handleClose is stable enough via refs; intentional omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleClose() {
    generation.current += 1;
    dayGeneration.current += 1;
    onClose?.();
    onFocusRestore?.();
  }

  async function loadDays(journeyId) {
    const token = ++dayGeneration.current;
    setDaysPhase('loading');
    setDaysError('');
    setDays([]);
    setSelectedDayId('');
    try {
      const schedulePayload = await journeyPlanningApi.getSchedule(journeyId);
      if (token !== dayGeneration.current || !mountedRef.current) return;
      const schedule = normalizePlanningSchedule(schedulePayload);
      setDays(Array.isArray(schedule.days) ? schedule.days : []);
      setDaysPhase('ready');
    } catch {
      if (token !== dayGeneration.current || !mountedRef.current) return;
      setDaysError(t('travel.personalMap.addToJourney.daysLoadFailed'));
      setDaysPhase('error');
    }
  }

  function goToDayStep() {
    if (!selectedJourneyId) return;
    setStep('day');
    loadDays(selectedJourneyId);
  }

  function buildHandoff(extra = {}) {
    if (!seed?.locationId) return null;
    return createPersonalMapJourneyHandoff({
      locationId: seed.locationId,
      displayName: seed.displayName,
      category: seed.category,
      sourceContext: 'personal-map',
      returnTo: '/me/personal-map',
      ...extra,
    });
  }

  function continueToPlanning() {
    if (!selectedJourneyId || !selectedDayId) return;
    const dayBelongs = days.some((d) => d.journeyDayId === selectedDayId);
    if (!dayBelongs) return;
    const handoff = buildHandoff({
      journeyId: selectedJourneyId,
      journeyDayId: selectedDayId,
    });
    if (!handoff) return;
    persistHandoffSession(handoff);
    navigate(`/journeys/${selectedJourneyId}/planning`, {
      state: wrapHandoffInLocationState(handoff),
    });
    handleClose();
  }

  function goCreateJourney() {
    const handoff = buildHandoff();
    if (!handoff) return;
    persistHandoffSession(handoff);
    navigate('/journeys/new', {
      state: wrapHandoffInLocationState(handoff),
    });
    handleClose();
  }

  function goEditJourneyWithoutDays() {
    if (!selectedJourneyId) return;
    const handoff = buildHandoff({ journeyId: selectedJourneyId });
    if (!handoff) return;
    persistHandoffSession(handoff);
    navigate(`/journeys/${selectedJourneyId}/planning`, {
      state: wrapHandoffInLocationState(handoff),
    });
    handleClose();
  }

  if (!open) return null;

  return (
    <div
      className="personal-map__add-sheet-scrim"
      data-testid="personal-map-add-sheet-scrim"
      onClick={handleClose}
      role="presentation"
    >
      <div
        ref={panelRef}
        className="personal-map__add-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="personal-map-add-sheet"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="personal-map__add-sheet-header">
          <h2 id={titleId}>
            {step === 'journey'
              ? t('travel.personalMap.addToJourney.selectJourneyTitle')
              : t('travel.personalMap.addToJourney.selectDayTitle')}
          </h2>
          <button
            type="button"
            className="personal-map__add-sheet-close"
            ref={closeRef}
            onClick={handleClose}
            data-testid="personal-map-add-sheet-close"
          >
            {t('travel.personalMap.addToJourney.cancel')}
          </button>
        </div>

        {seed?.displayName ? (
          <p className="personal-map__add-sheet-place" data-testid="personal-map-add-sheet-place">
            {seed.displayName}
          </p>
        ) : null}

        {step === 'journey' ? (
          <>
            {phase === 'loading' ? (
              <div className="personal-map__add-sheet-state" aria-live="polite" aria-busy="true" data-testid="personal-map-add-journeys-loading">
                <p>{t('travel.personalMap.addToJourney.loadingJourneys')}</p>
              </div>
            ) : null}

            {phase === 'error' ? (
              <div className="personal-map__add-sheet-state" role="alert" data-testid="personal-map-add-journeys-error">
                <Alert variant="error">{error}</Alert>
                <Button type="button" onClick={loadJourneys} data-testid="personal-map-add-journeys-retry">
                  {t('travel.personalMap.retry')}
                </Button>
              </div>
            ) : null}

            {phase === 'ready' && journeys.length === 0 ? (
              <div className="personal-map__add-sheet-state" data-testid="personal-map-add-journeys-empty">
                <p>{t('travel.personalMap.addToJourney.emptyTitle')}</p>
                <p>{t('travel.personalMap.addToJourney.emptyHint')}</p>
                <Button type="button" onClick={goCreateJourney} data-testid="personal-map-add-create-journey">
                  {t('travel.personalMap.addToJourney.createJourney')}
                </Button>
              </div>
            ) : null}

            {phase === 'ready' && journeys.length > 0 ? (
              <>
                <ul
                  className="personal-map__add-sheet-list"
                  id={listId}
                  aria-label={t('travel.personalMap.addToJourney.journeyListLabel')}
                  data-testid="personal-map-add-journeys-list"
                >
                  {journeys.map((journey) => {
                    const selected = selectedJourneyId === journey.journeyId;
                    return (
                      <li key={journey.journeyId}>
                        <button
                          type="button"
                          className={`personal-map__add-sheet-option${selected ? ' is-selected' : ''}`}
                          aria-pressed={selected}
                          onClick={() => setSelectedJourneyId(journey.journeyId)}
                          data-testid={`personal-map-add-journey-${journey.journeyId}`}
                        >
                          <span className="personal-map__add-sheet-option-title">{journey.title}</span>
                          {journey.dateRange ? (
                            <span className="personal-map__add-sheet-option-meta">{journey.dateRange}</span>
                          ) : null}
                          <span className="personal-map__add-sheet-option-mark" aria-hidden="true">
                            {selected ? '✓' : '○'}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <div className="personal-map__add-sheet-footer">
                  <Button
                    type="button"
                    disabled={!selectedJourneyId}
                    onClick={goToDayStep}
                    data-testid="personal-map-add-journey-next"
                  >
                    {t('travel.personalMap.addToJourney.nextDay')}
                  </Button>
                </div>
              </>
            ) : null}
          </>
        ) : null}

        {step === 'day' ? (
          <>
            <button
              type="button"
              className="personal-map__add-sheet-back"
              onClick={() => {
                dayGeneration.current += 1;
                setStep('journey');
                setSelectedDayId('');
                setDays([]);
                setDaysPhase('idle');
                setDaysError('');
              }}
              data-testid="personal-map-add-day-back"
            >
              {t('travel.personalMap.addToJourney.backToJourneys')}
            </button>

            {daysPhase === 'loading' ? (
              <div className="personal-map__add-sheet-state" aria-live="polite" aria-busy="true" data-testid="personal-map-add-days-loading">
                <p>{t('travel.personalMap.addToJourney.loadingDays')}</p>
              </div>
            ) : null}

            {daysPhase === 'error' ? (
              <div className="personal-map__add-sheet-state" role="alert" data-testid="personal-map-add-days-error">
                <Alert variant="error">{daysError}</Alert>
                <Button
                  type="button"
                  onClick={() => loadDays(selectedJourneyId)}
                  data-testid="personal-map-add-days-retry"
                >
                  {t('travel.personalMap.retry')}
                </Button>
              </div>
            ) : null}

            {daysPhase === 'ready' && days.length === 0 ? (
              <div className="personal-map__add-sheet-state" data-testid="personal-map-add-days-empty">
                <p>{t('travel.personalMap.addToJourney.daysEmptyTitle')}</p>
                <p>{t('travel.personalMap.addToJourney.daysEmptyHint')}</p>
                <Button type="button" onClick={goEditJourneyWithoutDays} data-testid="personal-map-add-go-planning">
                  {t('travel.personalMap.addToJourney.goPlanning')}
                </Button>
              </div>
            ) : null}

            {daysPhase === 'ready' && days.length > 0 ? (
              <>
                <ul
                  className="personal-map__add-sheet-list"
                  aria-label={t('travel.personalMap.addToJourney.dayListLabel')}
                  data-testid="personal-map-add-days-list"
                >
                  {days.map((day, index) => {
                    const selected = selectedDayId === day.journeyDayId;
                    return (
                      <li key={day.journeyDayId}>
                        <button
                          type="button"
                          className={`personal-map__add-sheet-option${selected ? ' is-selected' : ''}`}
                          aria-pressed={selected}
                          onClick={() => setSelectedDayId(day.journeyDayId)}
                          data-testid={`personal-map-add-day-${day.journeyDayId}`}
                        >
                          <span className="personal-map__add-sheet-option-title">
                            {dayLabel(day, index, t)}
                          </span>
                          <span className="personal-map__add-sheet-option-mark" aria-hidden="true">
                            {selected ? '✓' : '○'}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <div className="personal-map__add-sheet-footer">
                  <Button
                    type="button"
                    disabled={!selectedDayId}
                    onClick={continueToPlanning}
                    data-testid="personal-map-add-day-confirm"
                  >
                    {t('travel.personalMap.addToJourney.confirmDay')}
                  </Button>
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
